import { CheckIcon } from '@heroicons/react/24/solid'
import { useMemo } from 'react'

import {
  Carriers,
  type DocumentType,
  documentTypes,
} from '@freightview/carriers/ts'

import { matchesCarrierSearch } from './matchesCarrierSearch.js'

/** Columns shown on the document support tab (common types first). */
export const DOCUMENT_TYPE_COLUMNS: DocumentType[] = [
  'signed-bol',
  'invoice',
  'proof-of-delivery',
  'corrected-bol',
  'packing-slip',
  'customs-documents',
  'delivery-photo',
  'weight-cert',
  'other',
]

export const EXTRA_DOCUMENT_TYPE_COLUMNS = documentTypes.filter(
  type => !DOCUMENT_TYPE_COLUMNS.includes(type),
)

export interface DocumentCarrierSupport {
  name: string
  scac: string
  documentTypes: ReadonlySet<DocumentType>
}

export const documentCarrierSupport: DocumentCarrierSupport[] = Object.values(
  Carriers,
)
  .filter(carrier => carrier.documentTypes && carrier.documentTypes.length > 0)
  .map(carrier => ({
    name: carrier.name,
    scac: carrier.scac,
    documentTypes: new Set(carrier.documentTypes ?? []),
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

export const ALL_DOCUMENT_COLUMNS = [
  ...DOCUMENT_TYPE_COLUMNS,
  ...EXTRA_DOCUMENT_TYPE_COLUMNS,
]

export function DocumentTypesTable({ search }: { search: string }) {
  const carriers = useMemo(
    () =>
      documentCarrierSupport.filter(carrier =>
        matchesCarrierSearch(carrier, search),
      ),
    [search],
  )

  const totals = ALL_DOCUMENT_COLUMNS.map(type => ({
    type,
    count: documentCarrierSupport.filter(c => c.documentTypes.has(type)).length,
  }))

  return (
    <div className="overflow-auto rounded-box border border-base-300 bg-base-100">
      <table className="table table-sm table-pin-rows">
        <thead>
          <tr>
            <th className="bg-base-100">Carrier</th>
            <th className="bg-base-100">SCAC</th>
            {ALL_DOCUMENT_COLUMNS.map(type => (
              <th
                key={type}
                className="bg-base-100 text-center text-xs whitespace-nowrap"
              >
                {formatDocumentTypeLabel(type)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {carriers.map(carrier => (
            <tr key={carrier.scac} className="hover">
              <td className="font-medium whitespace-nowrap">{carrier.name}</td>
              <td className="font-mono uppercase opacity-70">{carrier.scac}</td>
              {ALL_DOCUMENT_COLUMNS.map(type => (
                <td key={type} className="text-center">
                  {carrier.documentTypes.has(type) ? (
                    <CheckIcon
                      className="mx-auto h-4 w-4 text-success"
                      aria-label={formatDocumentTypeLabel(type)}
                    />
                  ) : (
                    <span className="sr-only">Not supported</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="bg-base-100">Totals</th>
            <th className="bg-base-100" />
            {totals.map(({ type, count }) => (
              <th key={type} className="bg-base-100 text-center text-xs">
                {count}
              </th>
            ))}
          </tr>
        </tfoot>
      </table>
      {carriers.length === 0 ? (
        <div className="p-8 text-center text-sm opacity-70">
          No carriers match your search.
        </div>
      ) : null}
    </div>
  )
}
export const formatDocumentTypeLabel = (type: string): string =>
  type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
